import React, { useState, useEffect } from 'react';

/**
 * Groups timeline items by "year marker"—an item with no `company` and no `content`.
 * Returns an array of objects: { marker: string, items: [] }
 */
function groupTimeline(timeline) {
  const groups = [];
  let currentGroup = null;

  timeline.forEach((item) => {
    const isYearMarker = !item.company && !item.content;
    if (isYearMarker) {
      if (currentGroup) {
        groups.push(currentGroup);
      }
      currentGroup = { marker: item.date, items: [] };
    } else {
      if (!currentGroup) {
        currentGroup = { marker: '', items: [] };
      }
      currentGroup.items.push(item);
    }
  });

  if (currentGroup) {
    groups.push(currentGroup);
  }

  return groups;
}

function Showcase() {
  // LLM Timeline data
  const llmTimeline = [
    { date: '2022: Pre-training / Scaling Law / Chatbot' },
    { date: 'Mar 15th, 2022', company: 'OpenAI', content: 'OpenAI released GPT-3.5 text-davinci-002 API' },
    { date: 'Nov 30th, 2022', company: 'OpenAI', content: 'OpenAI released ChatGPT w/ GPT-3.5' },
    { date: '2023: Post-training / Agent' },
    { date: 'Jan 23rd, 2023', company: 'OpenAI', content: 'Microsoft announced a new multiyear investment in OpenAI, amounting to $10 billion. This investment valued OpenAI at approximately $29 billion' },
    { date: 'Mar 14th, 2023', company: 'OpenAI', content: 'OpenAI released GPT-4 which continues to follow the scaling law' },
    { date: 'Oct 10th, 2023', company: 'Benchmark', content: <><a className="author-link" href="https://www.swebench.com/" target="_blank" rel="noopener noreferrer">SWE-bench</a>, a major benchmark for evaluating LLM code generation performance with real-world GitHub issues, was released. RAG combined with ChatGPT-3.5 achieved only 0.33% on SWE-bench Lite</> },
    { date: '2024: Reasoning / Virtual Productivity' },
    { date: 'May 13th, 2024', company: 'OpenAI', content: 'OpenAI released GPT-4o, the first mainstream multimodal model' },
    { date: 'Jun 20th, 2024', company: 'Anthropic', content: 'Anthropic released Claude 3.5 Sonnet, which leads in programming capabilities and becomes a major competitor to OpenAI' },
    { date: 'Sep 20th, 2024', company: 'OpenAI', content: 'OpenAI released o1-preview, 1st reasoning model' },
    { date: 'Oct 2nd, 2024', company: 'OpenAI', content: 'OpenAI announced the completion of a $6.6 billion funding round, elevating valuation to $157 billion' },
    { date: 'Oct 22th, 2024', company: 'Anthropic', content: 'Anthropic released computer use, the first mainstream agent capable of operating a computer interface' },
    { date: '2025: Open source / Social Impact / Next Wave' },
    { date: 'Jan 20th, 2025', company: 'Deepseek', content: 'Deepseek released Deepseek-R1, 1st open-weights reasoning model. It brings significant benefits to robotics, ushering in a new analogical timeline' }
  ];

  // Robot Timeline data
  const robotTimeline = [
    { date: '2025: RL / VLM / Reasoning' },
    {
      date: 'Feb 20th, 2025',
      company: 'Figure',
      content: (
        <>
        Figure released <a className="author-link" href="https://www.figure.ai/news/helix" target="_blank" rel="noopener noreferrer">reasoning robot with collaboration</a>, valuation jumped from $2.6B to $39.5B
        </>
        )
    },
    {
      date: 'Mar 12th, 2025',
      company: 'Google',
      content: (
      <>
      Google released <a className="author-link" href="https://deepmind.google/discover/blog/gemini-robotics-brings-ai-into-the-physical-world/" target="_blank" rel="noopener noreferrer">Gemini Robotics</a> model
      </>
      )},
      {
        date: 'Mar 18th, 2025',
        company: 'Nvidia',
        content: (
        <>
        Nvidia released <a className="author-link" href="https://nvidianews.nvidia.com/news/nvidia-isaac-gr00t-n1-open-humanoid-robot-foundation-model-simulation-frameworks" target="_blank" rel="noopener noreferrer">GR00T N1</a> model, the world's first open humanoid robot foundation model
        </>
        )},
    { date: '2026: Mass Production / Human-Robot Interaction' },
    {
      date: 'By early 2026',
      company: 'Predict',
      content: 'Many companies, individuals, and investors are pouring into robotics industry, boosting the valuations of leading robotics startups',
    },
    {
      date: 'By mid 2026',
      company: 'Predict',
      content: 'The robotics industry is maturing, allowing leading companies to produce >10k units annually',
    },
    {
      date: 'By late 2026',
      company: 'Predict',
      content: 'As robot prices decline, their prevalence increases, making robotics and HRI increasingly popular fields. This trend drives more real-world oriented benchmarks',
    },
    { date: '2027: Physical Productivity' },
    {
      date: 'By mid 2027',
      company: 'Predict',
      content: 'The US market landscape features a mix of ~3 unicorn startups coexisting with major tech companies',
    },
    {
      date: 'By late 2027',
      company: 'Predict',
      content: 'Mass-produced robots (>1m) are increasingly automating physical work in real world, especially in developed countries where labor cost is expensive',
    },
    { date: '2028: Open Source / Social Impact / Next Wave' },
    {
      date: 'By early 2028',
      company: 'Predict',
      content: 'More companies are entering the market and adopting open-source strategies, significantly reducing the cost of robots. This further expands their applications and creates a broader societal impact',
    },
    {
      date: 'By mid 2028',
      company: 'Predict',
      content: 'As robots take over a significant amount of physical labor, people have more free time, which is increasingly directed toward social interactions and entertainment. This shift has driven the development of AR/VR, ushering in a new analogical timeline',
    },
  ];

  const llmGroups = groupTimeline(llmTimeline);
  const robotGroups = groupTimeline(robotTimeline);

  const [llmOpenStates, setLlmOpenStates] = useState(llmGroups.map(() => true));
  const [robotOpenStates, setRobotOpenStates] = useState(robotGroups.map(() => true));

  function toggleLlmGroup(index) {
    setLlmOpenStates((prev) => {
      const copy = [...prev];
      copy[index] = !copy[index];
      return copy;
    });
  }

  function toggleRobotGroup(index) {
    setRobotOpenStates((prev) => {
      const copy = [...prev];
      copy[index] = !copy[index];
      return copy;
    });
  }

  useEffect(() => {
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://platform.twitter.com/widgets.js';
    script.charset = 'utf-8';
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  function getIconByCompany(company) {
    switch (company) {
      case 'OpenAI':
        return '/images/OpenAI.png';
      case 'Deepseek':
        return '/images/Deepseek.png';
      case 'Figure':
        return '/images/Figure.png';
      case 'Google':
        return '/images/Google.png';
      case 'Nvidia':
        return '/images/NVIDIA.png';
      case 'Anthropic':
        return '/images/Anthropic.png';
      case 'Benchmark':
        return '/images/Histogram.jpg';
      case 'Predict':
        return '/images/Q.png';
      default:
        return '/images/OpenAI.png';
    }
  }

  return (
    <div style={{ position: 'relative', padding: '0', margin: '0' }}>
      <style>{`
        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }
        body {
          font-family: Arial, sans-serif;
          background: #fff;
        }
        /* Visually hidden text for accessibility */
        .sr-only {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
        }
        /* ================= Introduction Section ================= */
        .section-intro {
          background-color: #E6F2FF;
          padding: 2rem 1rem;
          text-align: center;
        }
        .section-intro h3 {
          font-size: 2rem;
          margin-bottom: 0.5rem;
          color: #333;
        }
        .section-intro h4 {
          font-size: 1.3rem;
          font-weight: 400;
          margin-bottom: 1.5rem;
          color: #666;
        }
        .author-info {
          margin-bottom: 1rem;
        }
        .author-link {
          color: #000;
          text-decoration: none;
          font-weight: bold;
          transition: color 0.2s;
        }
        .author-link:hover {
          color: #555;
          text-decoration: underline;
        }
        .intro-text {
          max-width: 980px;
          margin: 0 auto;
          text-align: left;
          line-height: 1.6;
          font-size: 1rem;
          color: #333;
          display: flex;
          align-items: flex-start;
          gap: 0.5em;
          /* Ensure text does not overflow */
          overflow-wrap: break-word;
          word-wrap: break-word;
          word-break: break-word;
        }
        .intro-text img {
          width: 300px;
          height: 300px;
          max-width: 100%;
          object-fit: contain;
          padding-right: 2em;
        }
        .intro-text .text-block {
          padding-top: 1em;
        }
        .intro-text p {
          overflow-wrap: break-word;
          word-wrap: break-word;
          word-break: break-word;
        }

        /* ================= Timeline Section ================= */
        .timeline-section-container {
          background-color: #f8f9f9;
          padding: 2rem 1rem;
        }
        .timeline-section-title {
          text-align: center;
          margin-bottom: 1.5rem;
          font-size: 1rem;
          color: #333;
        }
        .timeline-section {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 2rem;
          max-width: 1100px;
          margin: 0 auto;
        }
        .timeline-wrapper {
          flex: 1;
          position: relative;
          padding: 0 20px;
        }
        .timeline-wrapper h2 {
          text-align: center;
          margin-bottom: 1.5rem;
          font-size: 1.2rem;
          color: #333;
        }
        .timeline-wrapper::before {
          content: '';
          position: absolute;
          left: 30px;
          top: 0;
          width: 4px;
          height: 100%;
          background: linear-gradient(to bottom, #ff7f50, #ffd080);
          border-radius: 2px;
        }
        .timeline-item {
          position: relative;
          margin: 0.8rem 0;
          padding-left: 70px;
          opacity: 0;
          animation-duration: 0.8s;
          animation-fill-mode: forwards;
        }
        .timeline-icon {
          position: absolute;
          left: 10px;
          top: 10px;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
          background-color: #fff;
        }
        .timeline-icon img {
          width: 100%;
          height: auto;
          object-fit: contain;
        }
        .timeline-details {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .timeline-date {
          font-weight: bold;
          color: #555;
        }
        .timeline-content {
          background-color: #f7f7f7;
          padding: 10px 15px;
          border-radius: 8px;
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
          border-left: 3px solid #ff7f50;
          /* Ensure text does not overflow */
          overflow-wrap: break-word;
          word-wrap: break-word;
          word-break: break-word;
        }
        .timeline-item::before {
          content: '';
          position: absolute;
          left: 28px;
          top: 20px;
          width: 12px;
          height: 12px;
          background-color: #ff7f50;
          border-radius: 50%;
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
        }
        @keyframes fadeInLeft {
          from { transform: translateX(-30px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes fadeInRight {
          from { transform: translateX(30px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .fade-in-left { animation-name: fadeInLeft; }
        .fade-in-right { animation-name: fadeInRight; }
        .year-marker {
          display: inline-block;
          margin: 0.5rem 0;
          padding: 0.4rem 0.8rem;
          font-size: 1rem;
          font-weight: bold;
          color: #333;
          background: #fff;
          border: 1px solid #ddd;
          border-radius: 6px;
          box-shadow: 0 2px 5px rgba(0,0,0,0.05);
          cursor: pointer;
          transition: background-color 0.2s, box-shadow 0.2s;
        }
        .year-marker:hover {
          background-color: #f7f7f7;
          box-shadow: 0 3px 7px rgba(0,0,0,0.1);
        }
        .year-marker::before { display: none; }
        .year-marker .timeline-icon { display: none; }

        /* ================= Event Section ================= */
        .event-section {
          background-color: #ebedef;
          padding: 3rem 1rem;
        }
        .event-section .section-title {
          text-align: center;
          font-size: 2rem;
          margin-bottom: 2rem;
          color: #333;
        }
        .event-container {
          max-width: 1100px;
          margin: 0 auto;
          display: flex;
          flex-direction: row;
          align-items: flex-start;
          justify-content: center;
          gap: 2rem;
          background: #ffffff;
          border-radius: 12px;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
          padding: 2rem;
        }
        .event-image {
          width: 40%;
          max-width: 100%;
          height: auto;
          object-fit: cover;
          border-radius: 8px;
        }
        .event-info {
          width: 60%;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }
        .event-time-location-organizer {
          display: flex;
          flex-direction: row;
          gap: 3rem;
        }
        .event-time-location, .event-organizers {
          flex: 1;
        }
        .event-time-location h3,
        .event-organizers h3 {
          margin: 0;
          margin-bottom: 0.8rem;
          font-size: 1.3rem;
          color: #333;
        }
        .location-list {
          list-style: none;
          padding: 0;
          margin: 0;
          line-height: 1.8;
        }
        .location-list li {
          margin-bottom: 0.4rem;
          overflow-wrap: break-word;
          word-wrap: break-word;
          word-break: break-word;
        }
        .event-organizers {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .organizer {
          display: flex;
          align-items: center;
          gap: 0.8rem;
        }
        .organizer img {
          width: 50px;
          height: 50px;
          object-fit: cover;
          border-radius: 50%;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        .organizer-info {
          display: flex;
          flex-direction: column;
          line-height: 1.3;
        }
        .rsvp-container {
          margin-top: 2rem;
          text-align: center;
        }
        .rsvp-button {
          display: inline-block;
          background-color: #ff7f50;
          color: #fff;
          padding: 1rem 2rem;
          border-radius: 8px;
          font-weight: bold;
          text-decoration: none;
          font-size: 1.1rem;
          transition: background-color 0.2s;
        }
        .rsvp-button:hover {
          background-color: #ff9f70;
        }

        /* =========== Responsive Design =========== */
        @media (max-width: 768px) {
          .intro-text {
            flex-direction: column;
            align-items: center;
            text-align: center;
          }
          .intro-text img {
            width: 80%;
            height: auto;
            padding-right: 0;
            margin-bottom: 1em;
          }
          .timeline-section {
            flex-direction: column;
            align-items: center;
          }
          .timeline-wrapper {
            padding: 0;
            margin-bottom: 2rem;
          }
          .event-container {
            flex-direction: column;
            padding: 1rem;
          }
          .event-image, .event-info {
            width: 100%;
          }
          .event-time-location-organizer {
            flex-direction: column;
            gap: 1rem;
          }
        }

        @media (max-width: 480px) {
          .section-intro h3 {
            font-size: 1.5rem;
          }
          .section-intro h4 {
            font-size: 1.1rem;
          }
          .timeline-section-title {
            font-size: 1.5rem;
          }
          .timeline-wrapper h2 {
            font-size: 1rem;
          }
          .rsvp-button {
            font-size: 1rem;
            padding: 0.8rem 1.5rem;
          }
        }
      `}</style>

      {/* ============== Introduction Section ============== */}
      <div className="section-intro">
        <h3>Robot in 2025 as GenAI in 2022</h3>
        <h4>Riding the robot wave with lessons learned from GenAI boom</h4>
        <div
  className="author-info"
  style={{
    textAlign: "center", // 整体居中
    marginTop: "1em"
  }}
>
  <div>
    {/* <a
      className="author-link"
      href="https://jason-ding.com"
      target="_blank"
      rel="noopener noreferrer"
      style={{
        fontWeight: "bold",
        fontSize: "1.1rem",
        textDecoration: "none",
        color: "#000"
      }}
    > */}
      Zijian "Jason" Ding
    {/* </a> */}
    <div style={{ paddingTop: "0.2em" }}>PhD candidate</div>
    <div style={{ paddingTop: "0.2em" }}>University of Maryland, College Park</div>
  </div>
  {/* 社交链接按钮容器 */}
  <div
  className="social-links"
  style={{
    marginTop: "0.5em",
    display: "flex",
    justifyContent: "center", // 按钮在同一行居中
    gap: "10px",
    flexWrap: "wrap"
  }}
>
  {/* Website 按钮：深灰色 */}
  <a
    href="https://jason-ding.com"
    target="_blank"
    rel="noopener noreferrer"
    style={{
      textDecoration: "none",
      backgroundColor: "#85929e",   // 深灰色
      color: "#fff",
      padding: "8px 12px",
      borderRadius: "15px",     // 边角更圆滑
      fontSize: "0.9rem",
      fontWeight: "bold",
      transition: "background-color 0.3s"
    }}
    onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#666")}
    onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "#444")}
  >
    Website
  </a>

  {/* LinkedIn 按钮（可保持原样） */}
  <a
    href="https://www.linkedin.com/in/zijian-ding/"
    target="_blank"
    rel="noopener noreferrer"
    style={{
      textDecoration: "none",
      backgroundColor: "#0077B5",
      color: "#fff",
      padding: "8px 12px",
      borderRadius: "15px",
      fontSize: "0.9rem",
      fontWeight: "bold",
      transition: "background-color 0.3s"
    }}
    onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#005582")}
    onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "#0077B5")}
  >
    LinkedIn
  </a>

  {/* X 按钮：黑色 */}
  <a
    href="https://x.com/jasonzding"
    target="_blank"
    rel="noopener noreferrer"
    style={{
      textDecoration: "none",
      backgroundColor: "#000",   // 黑色
      color: "#fff",
      padding: "8px 12px",
      borderRadius: "15px",
      fontSize: "0.9rem",
      fontWeight: "bold",
      transition: "background-color 0.3s"
    }}
    onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#333")}
    onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "#000")}
  >
    X
  </a>
</div>
    </div>
        <div className="intro-text">
          <img src="/images/Robot.png" alt="Robot" />
          <div className="text-block">
            <p style={{ paddingBottom: '1em' }}>
              No need to regret not doing more during the GenAI boom—we are about to embrace an even bigger robotics wave soon.
            </p>
            <p style={{ paddingBottom: '1em' }}>
              Over the past year, we witnessed many AI advancements: VLM, open-source reasoning models,
              and multi-agent frameworks. All of these point towards robotics as a key application, as reflected in Google and Nvidia both releasing robotics models in March 2025.
              With great waves come loud tides—too many exciting opportunities in the robot wave
              makes it difficult to focus on the right things.
            </p>
            <p>
              Yet, when we look back at the history of GenAI, things become much clearer.
              Let’s discuss together the trial and error in GenAI back in 2022 to better ride the robot wave in 2025.
            </p>
          </div>
        </div>
      </div>

      {/* ============== Timeline Comparison Section ============== */}
      <div className="timeline-section-container">
        <h2
          className="timeline-section-title"
          style={{
            textAlign: 'center',
            fontSize: '2rem',
            marginBottom: '0.8rem'
          }}
        >
          Analogical Timeline for GenAI & Robot
        </h2>

        {/** 
         * The paragraph below has been given a maxWidth to prevent it from becoming too wide.
         */}
        <p
          style={{
            textAlign: 'center',
            maxWidth: '1100px',
            margin: '1rem auto 3rem auto',
            padding: '0 1rem',
            fontSize: '1.1rem'
          }}
        >
          Tech development is non-linear, making it difficult to accurately predict technology trajectory.
          Our best practice is to draw analogies from the GenAI timeline, though this approach is not
          always accurate. We will continuously update predictions to align with reality.
        </p>

        <div className="timeline-section">
          {/* LLM Timeline */}
          <div className="timeline-wrapper">
            <h2>GenAI since 2022</h2>
            {llmGroups.map((group, groupIndex) => {
              const isMarkerPresent = !!group.marker;
              const isOpen = llmOpenStates[groupIndex];

              return (
                <div key={groupIndex}>
                  {isMarkerPresent && (
                    <div
                      className="timeline-item year-marker fade-in-left"
                      style={{ animationDelay: `${groupIndex * 0.2}s` }}
                      onClick={() => toggleLlmGroup(groupIndex)}
                    >
                      {group.marker}
                    </div>
                  )}
                  {isOpen &&
                    group.items.map((item, itemIndex) => (
                      <div
                        className="timeline-item fade-in-left"
                        style={{ animationDelay: `${(groupIndex + itemIndex) * 0.2}s` }}
                        key={itemIndex}
                      >
                        <div className="timeline-icon">
                          <img src={getIconByCompany(item.company)} alt={item.company} />
                        </div>
                        <div className="timeline-details">
                          <div className="timeline-date">{item.date}</div>
                          <div className="timeline-content">
                            {item.content}
                            {item.link && (
                              <div style={{ marginTop: '10px' }}>
                                <blockquote className="twitter-tweet">
                                  <a href={item.link}>
                                    <span className="sr-only">View Embedded Tweet</span>
                                  </a>
                                </blockquote>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              );
            })}
          </div>

          {/* Robot Timeline */}
          <div className="timeline-wrapper">
            <h2>Robot since 2025</h2>
            {robotGroups.map((group, groupIndex) => {
              const isMarkerPresent = !!group.marker;
              const isOpen = robotOpenStates[groupIndex];

              return (
                <div key={groupIndex}>
                  {isMarkerPresent && (
                    <div
                      className="timeline-item year-marker fade-in-right"
                      style={{ animationDelay: `${groupIndex * 0.2}s` }}
                      onClick={() => toggleRobotGroup(groupIndex)}
                    >
                      {group.marker}
                    </div>
                  )}
                  {isOpen &&
                    group.items.map((item, itemIndex) => (
                      <div
                        className="timeline-item fade-in-right"
                        style={{ animationDelay: `${(groupIndex + itemIndex) * 0.2}s` }}
                        key={itemIndex}
                      >
                        <div className="timeline-icon">
                          <img src={getIconByCompany(item.company)} alt={item.company} />
                        </div>
                        <div className="timeline-details">
                          <div className="timeline-date">{item.date}</div>
                          <div className="timeline-content">
                            {item.content}
                            {item.link && (
                              <div style={{ marginTop: '10px' }}>
                                <blockquote className="twitter-tweet">
                                  <a href={item.link}>
                                    <span className="sr-only">View Embedded Tweet</span>
                                  </a>
                                </blockquote>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ============== Event Section ============== */}
      <div className="event-section">
        <h2 className="section-title">Upcoming Event</h2>
        <div className="event-container">
          <img src="/images/Atl.jpg" alt="Atlanta" className="event-image" />
          <div className="event-info">
            <h2 style={{ paddingBottom: "0.5em" }}>
              Colloquium: Robot in 2025 as GenAI in 2022
            </h2>
            <div className="event-time-location-organizer">
              <div className="event-time-location">
                <h3>Time & Location</h3>
                <ul className="location-list">
                  <li>
                    <strong>May 18th, 2025</strong>
                  </li>
                  <li>Georgia World Congress Center</li>
                  <li>Atlanta, GA</li>
                  <li>
                    (co-located w/{' '}
                    <a className="author-link" href="https://2025.ieee-icra.org/" target="_blank" rel="noopener noreferrer">
                      ICRA’25
                    </a>)
                  </li>
                </ul>
              </div>
              <div className="event-organizers">
                <h3>Organizers</h3>
                <div className="organizer">
                  <img src="/images/JD.png" alt="Zijian Ding" />
                  <div className="organizer-info">
                    <a className="author-link" href="https://jason-ding.com/" target="_blank" rel="noopener noreferrer">
                      Zijian Ding
                    </a>
                    <div>University of Maryland</div>
                  </div>
                </div>
                <div className="organizer">
                  <img src="/images/YB.png" alt="Yuanchen Bai" />
                  <div className="organizer-info">
                    <a className="author-link" href="https://byc-sophie.github.io/" target="_blank" rel="noopener noreferrer">
                      Yuanchen Bai
                    </a>
                    <div>Cornell University</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="rsvp-container">
              <a
                href="https://forms.gle/8Gy8gG2RYz49WiuX9"
                target="_blank"
                rel="noopener noreferrer"
                className="rsvp-button"
              >
                Free RSVP (limited seats)
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Showcase;